/**
 * MODULE: _shared/frontend-shell/src/hooks/assistant/streamOrchestrationAssistant.ts
 * PURPOSE: EventSource client for Langflow panel / dock → orchestration assistant stream.
 * DEPENDENCIES: @shared/lib/frontend/api/config, ./config
 * PUBLIC_API: streamOrchestrationAssistantText, parseStreamCompleteMetadata, mergeOrchestrationContinuationForSend,
 *             shouldDropStaleOrchestrationContinuation, formatReportResultFromStreamData, formatReportCompleteFromStreamData
 * SIDE_EFFECTS: Opens GET SSE to orchestration
 * AGENT_SAFETY: RESTRICTED
 * RECENT: 2026-07-15 [2.2.1591] — Meeting preview ``show_save_as_template`` for custom Help-me-ask invites.
 * RECENT_PRIOR: 2026-07-15 [2.2.1590] — Drop sticky schedule_meeting confirmation when user starts a fresh intent (e.g. Help me ask Contoso…).
 * RECENT_PRIOR: 2026-07-15 [2.2.1578] — Handle streaming ``report_complete`` (unwrap ``report_data``) so pipeline reports render on Langflow panel.
 * RECENT_PRIOR: 2026-07-15 [2.2.1563] — Hold ``action|tool === cancel_meeting`` confirm envelope so ``yes please`` resends continuation.
 * RECENT_PRIOR: 2026-07-11 [T1.0] — Unified-stream parity for CRM confirm-before-write: `clarification_needed` now handles `crud_update` (stage) + `crud_delete` (holds `continuation` so the next "yes"/"no" resolves the write); added `delete_confirmation`, `record_updated`, `record_deleted` listeners. Stage updates / deletes no longer render **(empty response)** on the unified path (R1/C1). Shared `appendAssistantLine` dedupe helper.
 * RECENT_PRIOR: 2026-07-09 [2.2.1493] — **`ui_component`** **`data_result`** → **`pendingDataResult`** (dock **`log_activity`** confirm no longer **(empty response)**).
 * RECENT_PRIOR: 2026-06-12 [2.2.1367] — **`formatAssistantQuotaExceededMessage()`**; no Langflow env hints on quota **429**; expanded **`isAssistantQuotaExceededError()`**.
 * RECENT_PRIOR: 2026-06-06 [2.2.1329] — **`clearPriorMessagePreview`** on **`record_created`** / **`message_sent`** / **`message_cancelled`** (Langflow panel preview card parity).
 * RECENT_PRIOR: 2026-06-06 [2.2.1328] — **`emailSendCoachingFingerprint`** dedupe on **`message_sent`** SSE retries; post-send coaching appended to thread.
 * RECENT_PRIOR: 2026-06-06 [2.2.1312] — **`parseStreamCompleteMetadata`** **`query_type`** → **`topic_slug`** fallback for platform tools and CRM writes.
 * RECENT: 2026-06-06 [2.2.1311] — Typing preview parity for **`draft-follow-up`**, **`assistant_platform_tools`**, **`create_contact`**.
 * RECENT_PRIOR: 2026-06-05 [2.2.1308] — Handle **`report_result`** SSE (pipeline KPI / export reports) on Langflow panel stream.
 * RECENT_PRIOR: 2026-06-04 — `data_result` SSE + DataTableCard on Langflow panel (list meetings/deals). Prior: approval_required + FormCard.
 */

import { API_CONFIG, getAuthToken, isAccessTokenExpired } from '@shared/lib/frontend/api/config';

import { normalizeDataTableRows } from '../../components/generative-ui/dataTableCells';

import {
  buildEmailSendCoachingMessage,
  emailSendCoachingFingerprint,
} from '../../components/assistant/emailTemplateSavedCoaching';
import { shouldSuppressAssistantNavigation } from './inferAssistantTopicFromMessage';
import { resolveContextQueryTypeFromStreamMetadata } from './assistantStreamContextQueryType';
import { parseOperatorPendingPromptFromMetadata } from './assistantOperatorMode';
import {
  parseStreamSuggestedActionsFromMetadata,
  type StreamSuggestedAction,
} from './streamSuggestedActions';
import { ASSISTANT_CONFIG } from './config';
import type {
  DataResult,
  EmailTemplateChoice,
  FormField,
  FormRequest,
  MessagePreview,
} from './types';

export type { StreamSuggestedAction } from './streamSuggestedActions';
export {
  parseStreamSuggestedActionsFromMetadata,
  streamSuggestedActionPrompts,
} from './streamSuggestedActions';

export type AssistantStreamRoutingMeta = {
  topicSlug?: string;
  langflowFlowId?: string;
  topicMatchConfidence?: number;
  /** Server-reported SSE timing from `complete.metadata.stream_timing_ms` [2.2.1388]. */
  ttftMs?: number;
  totalMs?: number;
  worldModelFetchMs?: number;
  /** Action-first / brief follow-up chips from complete.metadata [2.2.1481]. */
  suggestedNextActions?: StreamSuggestedAction[];
  /** Scoped query_type for POST /assistant/context/resolve after stream [2.2.1484]. */
  contextQueryType?: string;
  /** Last recommended step — user can reply "do it" to execute [2.2.1485]. */
  operatorPendingPrompt?: string;
};

export type OrchestrationStreamTurnResult = {
  text: string;
  /** When the server asks for a second turn (Gmail confirm, missing email body, etc.). */
  pendingContinuation?: Record<string, unknown>;
  /** Inline form (reschedule meeting, create deal, etc.). */
  pendingFormRequest?: FormRequest;
  /** Destructive tool paused until POST …/threads/{id}/approve. */
  pendingApproval?: OrchestrationApprovalRequired;
  /** Structured CRUD read table (meetings, deals, contacts). */
  pendingDataResult?: DataResult;
  /** Email template disambiguation picker (clarification_needed). */
  pendingEmailTemplateChoice?: EmailTemplateChoice;
  /** Review-before-send card (message_preview). */
  pendingMessagePreview?: MessagePreview;
  /** Clear preview cards on earlier bubbles after confirm/send/cancel (parity with useAssistantStream). */
  clearPriorMessagePreview?: boolean;
  /** Optional follow-up coaching after template send (message_sent). */
  postSendCoaching?: string;
};

export type OrchestrationApprovalRequired = {
  threadId: string;
  toolName: string;
  toolArgs: Record<string, unknown>;
  message: string;
};

export type StreamOrchestrationAssistantParams = {
  message: string;
  threadId: string;
  agentId: string;
  signal?: AbortSignal;
  /** Second-turn tool_continue envelope (JSON in stream query), same as useAssistantStream. */
  continuation?: Record<string, unknown>;
  /** Idle/module query type for first-turn routing (skipped when continuation is set). */
  queryTypePrefix?: string;
  /** Live shell path for per-turn grounding [2.2.1345]. */
  pagePath?: string;
  pageTitle?: string;
  /** Active dashboard widget id (maps to stream `canvas_id`) [2.2.1346]. */
  canvasId?: string;
  /** Shell focus entity (open account/deal/etc.) so the answer scopes to it [D1]. */
  focusEntityKind?: string;
  focusEntityId?: string;
  onAccumulated?: (text: string) => void;
  onActivity?: (label: string) => void;
  /** Fired on `complete` when orchestration returns topic routing metadata. */
  onRouting?: (meta: AssistantStreamRoutingMeta) => void;
  /** Fired when backend emits a `navigation` SSE event (SPA route change). */
  onNavigate?: (path: string, label?: string) => void;
};

/** User-facing text from `action_error` SSE payloads. */
export function formatActionErrorFromStreamData(data: Record<string, unknown>): string {
  const err = String(data.error || data.message || '').trim();
  return err || 'An error occurred while running that action.';
}


/** Map orchestration quota middleware JSON (HTTP 429) to assistant-friendly copy. */
export function formatAssistantQuotaExceededMessage(body: Record<string, unknown>): string {
  const detail = String(body.detail || '').trim();
  const detailSuffix =
    detail && !detail.toLowerCase().includes('current usage') ? ` ${detail}` : '';
  return (
    'Your AI usage quota has been exceeded for this workspace.' +
    `${detailSuffix} Open AI usage in Settings to review your limit and top up your plan.`
  );
}

/** True when an Error message already carries quota-exceeded copy (from 429 probe or SSE). */
export function isAssistantQuotaExceededError(message: string): boolean {
  const lower = (message || '').toLowerCase();
  return (
    lower.includes('ai token quota') ||
    lower.includes('token quota for this workspace') ||
    lower.includes('ai usage quota') ||
    lower.includes('quota exceeded') ||
    lower.includes('quota has been exceeded') ||
    lower.includes('reached your ai token') ||
    (lower.includes('current usage') && lower.includes('limit'))
  );
}

function streamUrlIncludesTokenQuery(streamUrl: string): boolean {
  try {
    const base =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';
    const parsed = streamUrl.startsWith('http') ? new URL(streamUrl) : new URL(streamUrl, base);
    return Boolean(parsed.searchParams.get('token')?.trim());
  } catch {
    return /[?&]token=/.test(streamUrl);
  }
}

/** When EventSource fails before tokens, probe the stream URL for a structured 429 body. */
export async function resolveAssistantStreamConnectionError(streamUrl: string): Promise<Error> {
  try {
    const headers: HeadersInit = {};
    // EventSource auth uses ?token=; Authorization on fetch adds CORS preflight and breaks the probe.
    if (!streamUrlIncludesTokenQuery(streamUrl)) {
      const token = getAuthToken();
      if (token && !isAccessTokenExpired(token)) {
        headers.Authorization = `Bearer ${token}`;
      }
    }
    const res = await fetch(streamUrl, { method: 'GET', credentials: 'include', headers });
    if (res.status === 429) {
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      return new Error(formatAssistantQuotaExceededMessage(body));
    }
  } catch {
    /* network / parse — fall through */
  }
  return new Error('Connection to the assistant was interrupted.');
}

function parseEventData(raw: string): Record<string, unknown> {
  try {
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function userLineImpliesDeleteEmailConfirm(line: string): boolean {
  const t = line.trim().toLowerCase();
  if (!t) return false;
  const exact = new Set([
    'yes',
    'yeah',
    'yep',
    'y',
    'ok',
    'okay',
    'sure',
    'confirm',
    'proceed',
    'trash it',
    'delete it',
    'go ahead',
    'do it',
  ]);
  if (exact.has(t)) return true;
  return /\b(yes|yeah|yep|ok|okay|sure|confirm|proceed|go\s*ahead|do\s*it|trash\s*it|delete\s*it)\b/i.test(
    t
  );
}

function userLineImpliesSendMessageCancel(line: string): boolean {
  const t = line.trim().toLowerCase();
  if (!t) return false;
  const exact = new Set(['no', 'n', 'cancel', "don't", 'discard', 'never mind', 'stop', 'abort']);
  if (exact.has(t)) return true;
  return /\b(no|cancel|discard|never\s*mind|stop|abort|don'?t\s+send)\b/i.test(t);
}

function isToolContinuationEnvelope(c: Record<string, unknown>): boolean {
  const t = c.tool;
  const action = c.action;
  return (
    t === 'send_email' ||
    t === 'reply_email' ||
    t === 'delete_email' ||
    t === 'cancel_meeting' ||
    t === 'send_message' ||
    t === 'log_activity' ||
    t === 'update_opportunity' ||
    t === 'schedule_meeting' ||
    action === 'schedule_meeting' ||
    // CRM cancel confirm (handler historically set action without tool) [2.2.1563]
    action === 'cancel_meeting'
  );
}

/**
 * True when the next send should NOT attach a pending confirmation envelope.
 * New chat clears the ref; this covers same-thread pivots (e.g. schedule invite open →
 * "Help me ask Jamie at Contoso about…") so the EventSource URL is not polluted with
 * stale ``schedule_meeting`` awaiting=confirmation [2.2.1590].
 */
export function shouldDropStaleOrchestrationContinuation(
  envelope: Record<string, unknown> | null | undefined,
  userMessage: string
): boolean {
  if (!envelope || Object.keys(envelope).length === 0) return false;
  const trimmed = userMessage.trim();
  if (!trimmed) return false;
  if (userLineImpliesDeleteEmailConfirm(trimmed)) return false;
  if (userLineImpliesSendMessageCancel(trimmed)) return false;

  const tool = String(envelope.tool ?? '').trim().toLowerCase();
  const action = String(envelope.action ?? '').trim().toLowerCase();
  const awaiting = String(envelope.awaiting ?? '').trim().toLowerCase();
  const isScheduleConfirm =
    (tool === 'schedule_meeting' || action === 'schedule_meeting') &&
    (awaiting === 'confirmation' || awaiting === '');

  if (!isScheduleConfirm) return false;

  // Fresh coaching / CRM / discovery intents — do not keep the invite confirm sticky.
  if (
    /^(help\s+me|what\s+should\s+i|give\s+me|suggest|generate|create|draft|show|list|research|send|add|update|delete|assign|route|open|navigate|brief|find|who|how)\b/i.test(
      trimmed
    )
  ) {
    return true;
  }
  // Multi-word lines that are not short affirms (affirm already returned false above).
  if (trimmed.split(/\s+/).length >= 5) return true;
  return false;
}

/**
 * Merge pending tool continuation with the user's follow-up line (mirrors useAssistantStream send()).
 */
export function mergeOrchestrationContinuationForSend(
  envelope: Record<string, unknown>,
  userMessage: string
): Record<string, unknown> {
  const trimmed = userMessage.trim();
  const pm = { ...envelope };
  if (
    (pm.tool === 'send_email' || pm.tool === 'reply_email') &&
    trimmed &&
    userLineImpliesSendMessageCancel(trimmed)
  ) {
    return { ...pm, cancelled: true };
  }
  if (
    (pm.tool === 'send_email' || pm.tool === 'reply_email') &&
    pm.collected_fields &&
    typeof pm.collected_fields === 'object' &&
    !Array.isArray(pm.collected_fields)
  ) {
    const cf = { ...(pm.collected_fields as Record<string, unknown>) };
    if (!cf.body && trimmed) cf.body = trimmed;
    return { ...pm, collected_fields: cf };
  }
  if (
    (pm.tool === 'delete_email' ||
      pm.tool === 'cancel_meeting' ||
      pm.action === 'cancel_meeting') &&
    pm.collected_fields &&
    typeof pm.collected_fields === 'object' &&
    !Array.isArray(pm.collected_fields)
  ) {
    const cf = { ...(pm.collected_fields as Record<string, unknown>) };
    if (!cf.confirm && trimmed && userLineImpliesDeleteEmailConfirm(trimmed)) {
      cf.confirm = true;
    }
    return { ...pm, collected_fields: cf };
  }
  if (
    (pm.tool === 'schedule_meeting' || pm.action === 'schedule_meeting') &&
    trimmed &&
    userLineImpliesDeleteEmailConfirm(trimmed)
  ) {
    return { ...pm, confirm: true, awaiting: 'confirmation' };
  }
  if (
    pm.tool === 'log_activity' &&
    pm.collected_fields &&
    typeof pm.collected_fields === 'object' &&
    !Array.isArray(pm.collected_fields)
  ) {
    const cf = { ...(pm.collected_fields as Record<string, unknown>) };
    if (!cf.confirm && trimmed && userLineImpliesDeleteEmailConfirm(trimmed)) {
      cf.confirm = true;
    }
    return { ...pm, collected_fields: cf };
  }
  if (
    pm.tool === 'update_opportunity' &&
    pm.collected_fields &&
    typeof pm.collected_fields === 'object' &&
    !Array.isArray(pm.collected_fields)
  ) {
    const cf = { ...(pm.collected_fields as Record<string, unknown>) };
    if (!cf.confirm && trimmed && userLineImpliesDeleteEmailConfirm(trimmed)) {
      cf.confirm = true;
    }
    return { ...pm, collected_fields: cf };
  }
  return pm;
}

/** Normalize orchestration `form_request` SSE payload for FormCard. */
export function parseFormRequestFromStreamData(
  data: Record<string, unknown>
): FormRequest | null {
  const fields = data.fields;
  if (!Array.isArray(fields)) return null;
  return {
    type: 'form_request',
    entity: String(data.entity || 'opportunity'),
    action: (data.action || 'create') as FormRequest['action'],
    fields: fields as FormField[],
    message: String(data.message || 'Please fill in the details below.'),
    continuation: data.continuation as FormRequest['continuation'],
    title: typeof data.title === 'string' ? data.title : undefined,
    formType: typeof data.formType === 'string' ? data.formType : undefined,
    mode: data.mode === 'edit' || data.mode === 'create' ? data.mode : undefined,
    entityType: typeof data.entityType === 'string' ? data.entityType : undefined,
    recordId: data.recordId != null ? String(data.recordId) : undefined,
  };
}

/** Normalize orchestration `approval_required` SSE payload for ApprovalModal. */
export function parseApprovalRequiredFromStreamData(
  data: Record<string, unknown>,
  threadId: string
): OrchestrationApprovalRequired | null {
  const pa = data.pending_approval;
  if (!pa || typeof pa !== 'object' || Array.isArray(pa)) return null;
  const rec = pa as Record<string, unknown>;
  const toolName = String(rec.tool_name ?? '').trim();
  if (!toolName) return null;
  const rawArgs = rec.tool_args;
  const toolArgs =
    rawArgs && typeof rawArgs === 'object' && !Array.isArray(rawArgs)
      ? (rawArgs as Record<string, unknown>)
      : {};
  const message =
    String(data.message ?? rec.message ?? '').trim() || `Approve "${toolName}"?`;
  return { threadId, toolName, toolArgs, message };
}

/** Normalize orchestration `clarification_needed` for email template picker. */
export function parseEmailTemplateChoiceFromStreamData(
  data: Record<string, unknown>
): EmailTemplateChoice | null {
  const cont =
    data.continuation && typeof data.continuation === 'object' && !Array.isArray(data.continuation)
      ? (data.continuation as Record<string, unknown>)
      : undefined;
  const awaiting = String(cont?.awaiting || '').trim();
  if (data.entity !== 'email_template' && awaiting !== 'template_pick') {
    return null;
  }
  return {
    type: 'email_template_choice',
    message: String(data.message || 'Which email template?'),
    matches: Array.isArray(data.matches) ? data.matches : [],
    continuation: cont,
  };
}

/**
 * Normalize a CRM confirm-before-write payload (`clarification_needed` for a stage/field update,
 * or `delete_confirmation`). Returns the prompt to show and the `continuation` to hold so the
 * user's next "yes"/"no" resolves the pending write. Returns null for non-CRM-confirm events
 * (e.g. email-template disambiguation) [T1.0].
 */
export function parseCrmConfirmFromStreamData(
  data: Record<string, unknown>
): { message: string; continuation?: Record<string, unknown> } | null {
  const cont =
    data.continuation && typeof data.continuation === 'object' && !Array.isArray(data.continuation)
      ? (data.continuation as Record<string, unknown>)
      : undefined;
  const action = String(cont?.action ?? '').toLowerCase();
  const isCrmConfirm =
    Boolean(data.pending_update) || action === 'crud_update' || action === 'crud_delete';
  if (!isCrmConfirm) return null;
  return {
    message: String(data.message ?? 'Please confirm.'),
    continuation: cont,
  };
}

/** Normalize orchestration `message_preview` SSE payload. */
export function parseMessagePreviewFromStreamData(
  data: Record<string, unknown>
): MessagePreview | null {
  if (!data.subject && !data.body && !data.recipient) return null;
  return {
    type: 'message_preview',
    recipient: String(data.recipient ?? 'team'),
    recipient_display: String(data.recipient_display ?? 'Team'),
    subject: String(data.subject ?? ''),
    body: String(data.body ?? ''),
    message: String(data.message ?? 'Review your message before sending:'),
    actions: Array.isArray(data.actions)
      ? (data.actions as MessagePreview['actions'])
      : [
          { label: 'Send', action: 'send_message', style: 'primary' },
          { label: 'Edit', action: 'edit_message', style: 'secondary' },
          { label: 'Cancel', action: 'cancel', style: 'secondary' },
        ],
    continuation: data.continuation as MessagePreview['continuation'],
    ...(data.show_save_as_template === true ? { show_save_as_template: true } : {}),
  };
}

/** Normalize `ui_component` SSE when component is `data_result` (tool_continue CRM writes). */
export function parseDataResultUiComponentFromStreamData(
  data: Record<string, unknown>
): DataResult | null {
  const comp = String(data.component || '').trim();
  if (comp !== 'data_result') return null;
  const props = data.props;
  if (!props || typeof props !== 'object' || Array.isArray(props)) return null;
  return parseDataResultFromStreamData(props as Record<string, unknown>);
}

/** Normalize orchestration `data_result` SSE payload for DataTableCard. */
export function parseDataResultFromStreamData(
  data: Record<string, unknown>
): DataResult | null {
  const columns = data.columns;
  const rows = data.rows;
  if (!Array.isArray(columns) || !Array.isArray(rows)) return null;
  const entity = String(data.entity || 'records');
  const totalRaw = data.total_count;
  const showingRaw = data.showing;
  const total_count =
    typeof totalRaw === 'number' && Number.isFinite(totalRaw) ? totalRaw : rows.length;
  const showing =
    typeof showingRaw === 'number' && Number.isFinite(showingRaw) ? showingRaw : rows.length;
  const summaryRaw = data.summary;
  const summary =
    summaryRaw && typeof summaryRaw === 'object' && !Array.isArray(summaryRaw)
      ? (summaryRaw as DataResult['summary'])
      : undefined;
  return {
    entity,
    columns: columns.map((c) => String(c)),
    rows: normalizeDataTableRows(rows),
    total_count,
    showing,
    message: typeof data.message === 'string' ? data.message : undefined,
    summary,
  };
}

/** Normalize `record_created` SSE (schedule meeting, CRM writes) into bubble text + optional table. */
export function parseRecordCreatedFromStreamData(
  data: Record<string, unknown>
): { message: string; dataResult?: DataResult } {
  const msg = String(data.message || 'Done.').trim();
  const toolPayload = data.data;
  const rawUiFromTool =
    toolPayload && typeof toolPayload === 'object'
      ? (toolPayload as { ui_component?: unknown }).ui_component
      : undefined;
  const rawUi = rawUiFromTool ?? data.ui_component;
  let dataResult: DataResult | undefined;
  if (rawUi != null && typeof rawUi === 'object' && !Array.isArray(rawUi)) {
    const ui = rawUi as Record<string, unknown>;
    const props =
      ui.component_type === 'data_result' && ui.props && typeof ui.props === 'object'
        ? (ui.props as Record<string, unknown>)
        : ui;
    const parsed = parseDataResultFromStreamData(props);
    if (parsed) dataResult = parsed;
  }
  return { message: msg, dataResult };
}

/** Format pipeline / export `report_result` SSE into assistant bubble markdown (mirrors useAssistantStream). */
export function formatReportResultFromStreamData(data: Record<string, unknown>): string {
  let reportText = typeof data.message === 'string' ? data.message : '';
  const sections = Array.isArray(data.sections) ? data.sections : [];
  for (const section of sections) {
    if (!section || typeof section !== 'object' || Array.isArray(section)) continue;
    const rec = section as Record<string, unknown>;
    reportText += `\n\n**${String(rec.heading || '').trim()}**\n\n`;
    const cols = Array.isArray(rec.columns) ? rec.columns.map((c) => String(c)) : [];
    const rows = Array.isArray(rec.rows) ? rec.rows : [];
    if (cols.length > 0) {
      reportText += `| ${cols.join(' | ')} |\n`;
      reportText += `| ${cols.map(() => '---').join(' | ')} |\n`;
      for (const row of rows) {
        if (!Array.isArray(row)) continue;
        reportText += `| ${row
          .map((cell) => String(cell ?? '').replace(/\|/g, '\\|'))
          .join(' | ')} |\n`;
      }
    }
  }
  const totals = data.totals;
  if (totals && typeof totals === 'object' && !Array.isArray(totals)) {
    // Skip when the lead message already carries the pipeline headline (avoids duplicate footer).
    const alreadySummarized =
      /total pipeline|\bdeals\s*·/i.test(reportText) ||
      Object.entries(totals as Record<string, unknown>).every(([k, v]) =>
        reportText.includes(String(v ?? '')) || reportText.toLowerCase().includes(k.toLowerCase())
      );
    if (!alreadySummarized) {
      reportText += '\n\n**Summary:**\n';
      for (const [k, v] of Object.entries(totals as Record<string, unknown>)) {
        reportText += `${k}: ${String(v ?? '')}\n`;
      }
    }
  }
  return reportText.trim();
}

/**
 * Streaming pipeline reports emit `report_complete` with nested `report_data.sections`
 * (see execute_report_stream). Unwrap so Langflow panel matches `report_result` markdown.
 */
export function formatReportCompleteFromStreamData(data: Record<string, unknown>): string {
  const nested =
    data.report_data && typeof data.report_data === 'object' && !Array.isArray(data.report_data)
      ? (data.report_data as Record<string, unknown>)
      : null;
  if (!nested) {
    const msg = typeof data.message === 'string' ? data.message.trim() : '';
    return msg;
  }
  return formatReportResultFromStreamData({
    message: typeof data.message === 'string' ? data.message : '',
    sections: nested.sections,
    totals: nested.totals,
  });
}

/** Orchestrator query_type values that should surface as topic chips when topic_slug is omitted. */
const ROUTE_QUERY_TYPE_AS_TOPIC: Record<string, string> = {
  assistant_platform_tools: 'assistant_platform_tools',
  crud_create_contact: 'create_contact',
  create_contact: 'create_contact',
  segment_account_patch: 'segment-account-patch',
};

/** Extract topic / Langflow routing fields from orchestration `complete` metadata. */
export function parseStreamCompleteMetadata(
  metadata: unknown
): AssistantStreamRoutingMeta | null {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return null;
  }
  const m = metadata as Record<string, unknown>;
  const explicitTopicSlug =
    typeof m.topic_slug === 'string'
      ? m.topic_slug
      : typeof m.agent_matched_topic_slug === 'string'
        ? m.agent_matched_topic_slug
        : undefined;
  const queryType = typeof m.query_type === 'string' ? m.query_type.trim() : '';
  const topicSlug =
    explicitTopicSlug?.trim() ||
    (queryType ? ROUTE_QUERY_TYPE_AS_TOPIC[queryType] : undefined);
  const langflowFlowId =
    typeof m.langflow_flow_id === 'string'
      ? m.langflow_flow_id
      : typeof m.agent_langflow_flow_id === 'string'
        ? m.agent_langflow_flow_id
        : undefined;
  const confRaw = m.topic_match_confidence ?? m.agent_topic_match_confidence;
  const topicMatchConfidence =
    typeof confRaw === 'number' && Number.isFinite(confRaw) ? confRaw : undefined;
  const timingRaw = m.stream_timing_ms;
  let ttftMs: number | undefined;
  let totalMs: number | undefined;
  let worldModelFetchMs: number | undefined;
  if (timingRaw && typeof timingRaw === 'object' && !Array.isArray(timingRaw)) {
    const t = timingRaw as Record<string, unknown>;
    if (typeof t.ttft_ms === 'number' && Number.isFinite(t.ttft_ms)) {
      ttftMs = t.ttft_ms;
    }
    if (typeof t.total_ms === 'number' && Number.isFinite(t.total_ms)) {
      totalMs = t.total_ms;
    }
    if (typeof t.world_model_fetch_ms === 'number' && Number.isFinite(t.world_model_fetch_ms)) {
      worldModelFetchMs = t.world_model_fetch_ms;
    }
  }
  const suggestedNextActions = parseStreamSuggestedActionsFromMetadata(m);
  const contextQueryType = resolveContextQueryTypeFromStreamMetadata(m);
  const operatorPendingPrompt = parseOperatorPendingPromptFromMetadata(m);
  if (
    !topicSlug &&
    !langflowFlowId &&
    topicMatchConfidence === undefined &&
    ttftMs === undefined &&
    totalMs === undefined &&
    worldModelFetchMs === undefined &&
    suggestedNextActions.length === 0 &&
    !contextQueryType &&
    !operatorPendingPrompt
  ) {
    return null;
  }
  return {
    topicSlug,
    langflowFlowId,
    topicMatchConfidence,
    ttftMs,
    totalMs,
    worldModelFetchMs,
    suggestedNextActions:
      suggestedNextActions.length > 0 ? suggestedNextActions : undefined,
    contextQueryType,
    operatorPendingPrompt,
  };
}

/** Prepend `[QUERY_TYPE: …]` on first turns (parity with `useAssistantStream`). */
export function buildOrchestrationStreamOutboundMessage(
  message: string,
  options?: { queryTypePrefix?: string; continuation?: Record<string, unknown> }
): string {
  const trimmed = (message || '').trim();
  const cont = options?.continuation;
  if (cont && Object.keys(cont).length > 0) {
    return trimmed;
  }
  const qt = (options?.queryTypePrefix ?? '').trim();
  if (!qt || qt === 'general_chat') {
    return trimmed;
  }
  if (/^\[QUERY_TYPE:\s*/i.test(trimmed)) {
    return trimmed;
  }
  return `[QUERY_TYPE: ${qt}]\n${trimmed}`;
}

/**
 * Stream one assistant turn via orchestration (same contract as `useAssistantStream`).
 * Handles `token`, `followup_question`, `tool_start`, `complete`, and `error` events.
 */
/**
 * Build the GET `/chat/stream` query string (pure — unit-tested). Includes the shell focus
 * entity (`focus_entity_kind`/`focus_entity_id`) so the answer scopes to the open record [D1].
 */
export function buildOrchestrationStreamQuery(
  params: StreamOrchestrationAssistantParams,
  token: string
): URLSearchParams {
  const outboundMessage = buildOrchestrationStreamOutboundMessage(params.message, {
    queryTypePrefix: params.queryTypePrefix,
    continuation: params.continuation,
  });
  const qs = new URLSearchParams({
    message: outboundMessage,
    thread_id: params.threadId,
    agent_id: params.agentId,
    token,
  });
  const pagePath = (params.pagePath ?? '').trim();
  if (pagePath) {
    qs.set('page_path', pagePath);
  }
  const pageTitle = (params.pageTitle ?? '').trim();
  if (pageTitle) {
    qs.set('page_title', pageTitle);
  }
  const canvasId = (params.canvasId ?? '').trim();
  if (canvasId) {
    qs.set('canvas_id', canvasId);
  }
  const focusEntityKind = (params.focusEntityKind ?? '').trim();
  const focusEntityId = (params.focusEntityId ?? '').trim();
  if (focusEntityKind && focusEntityId) {
    qs.set('focus_entity_kind', focusEntityKind);
    qs.set('focus_entity_id', focusEntityId);
  }
  if (params.continuation && Object.keys(params.continuation).length > 0) {
    qs.set('continuation', JSON.stringify(params.continuation));
  }
  return qs;
}

export async function streamOrchestrationAssistantText(
  params: StreamOrchestrationAssistantParams
): Promise<OrchestrationStreamTurnResult> {
  const token = await getAuthToken();
  if (!token || isAccessTokenExpired(token)) {
    throw new Error('Sign in required to use the assistant.');
  }

  const base = ASSISTANT_CONFIG.streamUrl.startsWith('http')
    ? ASSISTANT_CONFIG.streamUrl
    : `${API_CONFIG.baseURL}${ASSISTANT_CONFIG.streamUrl}`;

  const qs = buildOrchestrationStreamQuery(params, token);
  const streamUrl = `${base}?${qs.toString()}`;

  return new Promise<OrchestrationStreamTurnResult>((resolve, reject) => {
    const es = new EventSource(streamUrl);
    let full = '';
    let pendingContinuation: Record<string, unknown> | undefined;
    let pendingFormRequest: FormRequest | undefined;
    let pendingApproval: OrchestrationApprovalRequired | undefined;
    let pendingDataResult: DataResult | undefined;
    let pendingEmailTemplateChoice: EmailTemplateChoice | undefined;
    let pendingMessagePreview: MessagePreview | undefined;
    let clearPriorMessagePreview = false;
    let postSendCoaching: string | undefined;
    let postSendCoachingFingerprint: string | null = null;
    let assistantSummaryFromComplete: string | undefined;
    let settled = false;

    const applyFollowupPayload = (data: Record<string, unknown>) => {
      const msg = String(data.message || data.question || 'Could you provide more details?').trim();
      const norm = (s: string) => s.replace(/\s+/g, ' ').trim();
      if (msg && !norm(full).endsWith(norm(msg))) {
        full += (full ? '\n\n' : '') + msg;
        params.onAccumulated?.(full);
      }
      const cont = data.continuation;
      if (cont && typeof cont === 'object' && !Array.isArray(cont)) {
        const rec = cont as Record<string, unknown>;
        if (isToolContinuationEnvelope(rec)) {
          pendingContinuation = rec;
        }
      }
    };

    const applyFormRequestPayload = (data: Record<string, unknown>) => {
      const fr = parseFormRequestFromStreamData(data);
      if (fr) pendingFormRequest = fr;
    };

    const applyApprovalRequiredPayload = (data: Record<string, unknown>) => {
      const approval = parseApprovalRequiredFromStreamData(data, params.threadId);
      if (!approval) return;
      const norm = (s: string) => s.replace(/\s+/g, ' ').trim();
      if (approval.message && !norm(full).endsWith(norm(approval.message))) {
        full += (full ? '\n\n' : '') + approval.message;
        params.onAccumulated?.(full);
      }
      pendingApproval = approval;
    };

    const applyReportResultPayload = (data: Record<string, unknown>) => {
      const reportText = formatReportResultFromStreamData(data);
      if (!reportText) return;
      full = full ? `${full}\n\n${reportText}` : reportText;
      params.onAccumulated?.(full);
    };

    const applyReportCompletePayload = (data: Record<string, unknown>) => {
      if (data.status === 'error') {
        appendAssistantLine(data.message || 'Report failed.');
        return;
      }
      const reportText = formatReportCompleteFromStreamData(data);
      if (!reportText) return;
      full = full ? `${full}\n\n${reportText}` : reportText;
      params.onAccumulated?.(full);
    };

    const applyDataResultPayload = (data: Record<string, unknown>) => {
      const dr = parseDataResultFromStreamData(data);
      if (!dr) return;
      pendingDataResult = dr;
      // Table caption lives on DataTableCard — drop streamed duplicate caption from the bubble.
      const caption = dr.message?.trim();
      if (caption && dr.rows.length > 0) {
        const norm = (s: string) => s.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
        if (norm(full) === norm(caption) || norm(full).endsWith(norm(caption))) {
          full = '';
          params.onAccumulated?.(full);
        }
      }
    };

    /** Append a server-authored line to the assistant bubble (deduped against the tail). */
    const appendAssistantLine = (raw: unknown) => {
      const msg = String(raw ?? '').trim();
      if (!msg) return;
      const norm = (s: string) => s.replace(/\s+/g, ' ').trim();
      if (norm(full).endsWith(norm(msg))) return;
      full += full ? `\n\n${msg}` : msg;
      params.onAccumulated?.(full);
    };

    const finish = (err?: Error, text?: string) => {
      if (settled) return;
      settled = true;
      es.close();
      if (err) reject(err);
      else {
        const resolved = (text ?? full).trim();
        const structuredFallback =
          pendingDataResult?.message?.trim() ||
          assistantSummaryFromComplete ||
          (pendingFormRequest || pendingApproval || pendingContinuation ? '' : '');
        const hasStructuredUi =
          Boolean(pendingDataResult) ||
          Boolean(pendingFormRequest) ||
          Boolean(pendingApproval) ||
          Boolean(pendingMessagePreview);
        resolve({
          text: resolved || structuredFallback || (hasStructuredUi ? '' : '(empty response)'),
          pendingContinuation,
          pendingFormRequest,
          pendingApproval,
          pendingDataResult,
          pendingEmailTemplateChoice,
          pendingMessagePreview,
          clearPriorMessagePreview: clearPriorMessagePreview || undefined,
          postSendCoaching,
        });
      }
    };

    const onAbort = () => {
      es.close();
      if (!settled) {
        settled = true;
        reject(new DOMException('Aborted', 'AbortError'));
      }
    };
    params.signal?.addEventListener('abort', onAbort, { once: true });

    es.addEventListener('open', () => {
      params.onActivity?.('Processing…');
    });

    es.addEventListener('metadata', (e: MessageEvent) => {
      const data = parseEventData(String(e.data ?? ''));
      const intent =
        typeof data.intent === 'string'
          ? data.intent.trim()
          : typeof data.query_type === 'string'
            ? data.query_type.trim()
            : '';
      if (intent) {
        params.onActivity?.(`Routing: ${intent.replace(/_/g, ' ')}`);
      }
    });

    es.addEventListener('token', (e: MessageEvent) => {
      const data = parseEventData(String(e.data ?? ''));
      const chunk = typeof data.content === 'string' ? data.content : '';
      if (!chunk) return;
      if (full.length > 0 && full === chunk.trim()) return;
      full += chunk;
      params.onAccumulated?.(full);
    });

    es.addEventListener('followup_question', (e: MessageEvent) => {
      applyFollowupPayload(parseEventData(String(e.data ?? '')));
    });

    es.addEventListener('form_request', (e: MessageEvent) => {
      applyFormRequestPayload(parseEventData(String(e.data ?? '')));
    });

    es.addEventListener('approval_required', (e: MessageEvent) => {
      applyApprovalRequiredPayload(parseEventData(String(e.data ?? '')));
    });

    es.addEventListener('data_result', (e: MessageEvent) => {
      applyDataResultPayload(parseEventData(String(e.data ?? '')));
    });

    es.addEventListener('report_result', (e: MessageEvent) => {
      applyReportResultPayload(parseEventData(String(e.data ?? '')));
    });

    es.addEventListener('report_complete', (e: MessageEvent) => {
      applyReportCompletePayload(parseEventData(String(e.data ?? '')));
    });

    es.addEventListener('report_progress', (e: MessageEvent) => {
      const data = parseEventData(String(e.data ?? ''));
      if (data.status === 'error') {
        applyReportCompletePayload(data);
      }
    });

    es.addEventListener('action_error', (e: MessageEvent) => {
      const data = parseEventData(String(e.data ?? ''));
      const errMsg = formatActionErrorFromStreamData(data);
      full = errMsg;
      params.onAccumulated?.(full);
    });

    es.addEventListener('navigation', (e: MessageEvent) => {
      const data = parseEventData(String(e.data ?? ''));
      const path = String(data.path || '/').trim() || '/';
      const label = typeof data.label === 'string' ? data.label : undefined;
      if (
        shouldSuppressAssistantNavigation(params.message) ||
        pendingDataResult
      ) {
        return;
      }
      params.onNavigate?.(path, label);
    });

    es.addEventListener('clarification_needed', (e: MessageEvent) => {
      const data = parseEventData(String(e.data ?? ''));

      // Confirm-before-write for CRM stage updates / deletes (parity with useAssistantStream) [T1.0].
      // The backend puts this prompt only on the SSE event, not the token stream — surface it and
      // hold the continuation so the user's next "yes"/"no" reply resolves the pending write.
      const crmConfirm = parseCrmConfirmFromStreamData(data);
      if (crmConfirm) {
        appendAssistantLine(crmConfirm.message);
        if (crmConfirm.continuation) pendingContinuation = crmConfirm.continuation;
        return;
      }

      // Email-template disambiguation picker.
      const choice = parseEmailTemplateChoiceFromStreamData(data);
      if (!choice) return;
      pendingEmailTemplateChoice = choice;
      appendAssistantLine(choice.message);
      const choiceCont = choice.continuation;
      if (choiceCont && typeof choiceCont === 'object') {
        pendingContinuation = choiceCont;
      }
    });

    // Destructive delete confirmation (crud_delete) — distinct SSE event from clarification_needed [T1.0].
    es.addEventListener('delete_confirmation', (e: MessageEvent) => {
      const data = parseEventData(String(e.data ?? ''));
      const crmConfirm = parseCrmConfirmFromStreamData(data);
      appendAssistantLine(crmConfirm?.message ?? data.message ?? 'Are you sure?');
      if (crmConfirm?.continuation) pendingContinuation = crmConfirm.continuation;
    });

    // Turn-2 success after a confirmed write — surface the outcome (was dropped → empty bubble) [T1.0].
    es.addEventListener('record_updated', (e: MessageEvent) => {
      appendAssistantLine(parseEventData(String(e.data ?? '')).message ?? 'Updated.');
    });

    es.addEventListener('record_deleted', (e: MessageEvent) => {
      appendAssistantLine(parseEventData(String(e.data ?? '')).message ?? 'Deleted.');
    });

    es.addEventListener('delete_cancelled', (e: MessageEvent) => {
      appendAssistantLine(parseEventData(String(e.data ?? '')).message ?? 'Deletion cancelled.');
    });

    es.addEventListener('message_preview', (e: MessageEvent) => {
      const data = parseEventData(String(e.data ?? ''));
      const preview = parseMessagePreviewFromStreamData(data);
      if (!preview) return;
      pendingMessagePreview = preview;
      const msg = preview.message.trim();
      const norm = (s: string) => s.replace(/\s+/g, ' ').trim();
      if (msg && !norm(full).endsWith(norm(msg))) {
        full += full ? `\n\n${msg}` : msg;
        params.onAccumulated?.(full);
      }
      const cont = preview.continuation as Record<string, unknown> | undefined;
      if (
        cont &&
        (isToolContinuationEnvelope(cont) || cont.action === 'schedule_meeting')
      ) {
        pendingContinuation = cont;
      }
    });

    es.addEventListener('record_created', (e: MessageEvent) => {
      const data = parseEventData(String(e.data ?? ''));
      const { message, dataResult } = parseRecordCreatedFromStreamData(data);
      const norm = (s: string) => s.replace(/\s+/g, ' ').trim();
      if (message && !norm(full).endsWith(norm(message))) {
        full += full ? `\n\n${message}` : message;
        params.onAccumulated?.(full);
      }
      if (dataResult) {
        pendingDataResult = dataResult;
      }
      pendingMessagePreview = undefined;
      clearPriorMessagePreview = true;
    });

    es.addEventListener('message_cancelled', (e: MessageEvent) => {
      const data = parseEventData(String(e.data ?? ''));
      const msg = String(data.message || 'Cancelled.').trim();
      if (msg) {
        full = msg;
        params.onAccumulated?.(full);
      }
      pendingContinuation = undefined;
      pendingMessagePreview = undefined;
      clearPriorMessagePreview = true;
    });

    es.addEventListener('message_sent', (e: MessageEvent) => {
      const data = parseEventData(String(e.data ?? ''));
      const msg = String(data.message || 'Message sent.').trim();
      if (msg) {
        full += full ? `\n\n${msg}` : msg;
        params.onAccumulated?.(full);
      }
      pendingMessagePreview = undefined;
      pendingContinuation = undefined;
      clearPriorMessagePreview = true;
      const coaching = buildEmailSendCoachingMessage(data);
      const fp = emailSendCoachingFingerprint(data);
      if (coaching && fp !== postSendCoachingFingerprint) {
        postSendCoaching = coaching;
        postSendCoachingFingerprint = fp;
      }
    });

    es.addEventListener('ui_component', (e: MessageEvent) => {
      const data = parseEventData(String(e.data ?? ''));
      const comp = String(data.component || '');
      const props = (data.props || {}) as Record<string, unknown>;
      if (comp === 'data_result') {
        applyDataResultPayload(props);
        return;
      }
      if (comp === 'ErrorAlert' && typeof props.message === 'string') {
        const msg = props.message.trim();
        if (msg && !full.includes(msg)) {
          full += full ? `\n\n${msg}` : msg;
          params.onAccumulated?.(full);
        }
      }
    });

    es.addEventListener('tool_start', (e: MessageEvent) => {
      const data = parseEventData(String(e.data ?? ''));
      const tool = String(data.tool_name || data.tool || data.name || 'tool')
        .trim()
        .replace(/_/g, ' ');
      params.onActivity?.(`Running ${tool || 'tool'}…`);
    });

    es.addEventListener('error', (e: MessageEvent) => {
      if (settled) return;
      const raw = String(e.data ?? '');
      if (!raw.trim()) return;
      const data = parseEventData(raw);
      const rawMsg =
        typeof data.message === 'string'
          ? data.message
          : typeof data.error === 'string'
            ? data.error
            : 'Assistant stream failed.';
      const msg = isAssistantQuotaExceededError(rawMsg)
        ? formatAssistantQuotaExceededMessage(data)
        : rawMsg;
      finish(new Error(msg));
    });

    const handleCompletePayload = (raw: string) => {
      const data = parseEventData(raw);
      const meta = data.metadata;
      if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
        const m = meta as Record<string, unknown>;
        const summaryRaw = m.assistant_summary;
        if (typeof summaryRaw === 'string' && summaryRaw.trim()) {
          assistantSummaryFromComplete = summaryRaw.trim();
        }
        if (m.status === 'error') {
          finish(full.trim() ? undefined : new Error('Assistant completed with an error.'));
          return;
        }
        const routing = parseStreamCompleteMetadata(meta);
        if (routing) {
          params.onRouting?.(routing);
        }
      }
      finish(undefined, full);
    };

    es.addEventListener('complete', (e: MessageEvent) => {
      handleCompletePayload(String(e.data ?? ''));
    });

    es.addEventListener('message', (e: MessageEvent) => {
      const data = parseEventData(String(e.data ?? ''));
      if (data.type === 'followup_question') {
        applyFollowupPayload(data);
        return;
      }
      if (data.type === 'form_request') {
        applyFormRequestPayload(data);
        return;
      }
      if (data.type === 'approval_required') {
        applyApprovalRequiredPayload(data);
        return;
      }
      if (data.type === 'data_result') {
        applyDataResultPayload(data);
        return;
      }
      if (data.type === 'report_result') {
        applyReportResultPayload(data);
        return;
      }
      if (data.type === 'report_complete') {
        applyReportCompletePayload(data);
        return;
      }
      if (data.type === 'report_progress' && data.status === 'error') {
        applyReportCompletePayload(data);
        return;
      }
      if (data.type !== 'complete') return;
      handleCompletePayload(
        JSON.stringify({
          status: data.status ?? 'success',
          metadata: data.metadata,
        })
      );
    });

    es.onerror = () => {
      if (settled) return;
      const hasStructuredPayload = Boolean(
        pendingDataResult ||
          pendingFormRequest ||
          pendingApproval ||
          pendingContinuation ||
          pendingEmailTemplateChoice ||
          pendingMessagePreview
      );
      if (full.trim() || hasStructuredPayload) {
        finish(
          undefined,
          full.trim() || String(pendingDataResult?.message || '').trim()
        );
        return;
      }
      void resolveAssistantStreamConnectionError(streamUrl).then((err) => {
        if (!settled) finish(err);
      });
    };
  });
}
